import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  ISignUpResult,
} from "amazon-cognito-identity-js";
import { CognitoIdentity } from "aws-sdk";
import * as AWS from "aws-sdk/global";
import { CreateAccountFormValues } from "../forms/account/create-account-form";
import { LoginFormValues } from "../forms/auth/login-form";
import { AccountService } from "./account-service";

const AWS_REGION = process.env.REACT_APP_AWS_REGION as string;
const AWS_CLIENT_ID = process.env.REACT_APP_AWS_CLIENT_ID as string;
const AWS_USER_POOL_ID = process.env.REACT_APP_AWS_USER_POOL_ID as string;
const AWS_IDENTITY_POOL_ID = process.env
  .REACT_APP_AWS_IDENTITY_POOL_ID as string;

export const CognitoService = {
  userPool(): CognitoUserPool {
    return new CognitoUserPool({
      UserPoolId: AWS_USER_POOL_ID,
      ClientId: AWS_CLIENT_ID,
    });
  },
  getCurrentUser(): CognitoUser | null {
    return this.userPool().getCurrentUser();
  },
  signUp(data: CreateAccountFormValues): Promise<ISignUpResult> {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser()) return reject("Already signed in");

      const userAttributes = [
        new CognitoUserAttribute({ Name: "email", Value: data.email }),
      ];

      this.userPool().signUp(
        data.email,
        data.password,
        userAttributes,
        userAttributes,
        (err, signUpResult) => {
          if (signUpResult) {
            AccountService.createAccount({
              id: signUpResult.userSub,
              firstName: data.firstName,
              lastName: data.lastName,
              county: data.county,
              municipality: data.municipality,
              username: data.email,
              email: data.email,
              phone: data.phone,
              city: data.city,
              stateId: data.state,
              postalCode: data.zipCode,
              address1: data.streetAddress,
              address2: data.aptSuiteBuilding,
            })
              .then(() => resolve(signUpResult))
              .catch((err) => reject(err?.message || err));
          }
          if (err) reject(err.message);
        }
      );
    });
  },
  authenticateUser(data: LoginFormValues): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser()) return reject("Already signed in");

      const cognitoUser = new CognitoUser({
        Username: data.email,
        Pool: this.userPool(),
      });

      const authDetails = new AuthenticationDetails({
        Username: data.email,
        Password: data.password,
      });

      cognitoUser.authenticateUser(authDetails, {
        onFailure: (err) => reject(err.message),
        onSuccess(session) {
          AWS.config.region = AWS_REGION;

          AWS.config.credentials = new AWS.CognitoIdentityCredentials({
            IdentityPoolId: AWS_IDENTITY_POOL_ID,
            Logins: {
              [`cognito-idp.${AWS_REGION}.amazonaws.com/${AWS_USER_POOL_ID}`]:
                session.getIdToken().getJwtToken(),
            },
          });

          (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(
            (err) => {
              if (err) reject(err.message);
            }
          );

          return resolve(session);
        },
      });
    });
  },
  signOut(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        const currentUser = this.getCurrentUser();
        if (!currentUser) return resolve(true);
        currentUser?.signOut(() => resolve(true));
      } catch (err) {
        return reject((err as Error).message);
      }
    });
  },
  changeEmail(newEmail: string) {
    return new Promise((resolve, reject) => {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return reject("Not signed in");

      const userAttributes = [
        new CognitoUserAttribute({ Name: "email", Value: newEmail }),
      ];

      currentUser.getSession(
        (err: null | Error, session: null | CognitoUserSession) => {
          if (err) return reject(err.message);
          if (session) {
            currentUser.updateAttributes(userAttributes, (err, result) => {
              if (err) return reject(err.message);
              if (result) return resolve(result);
            });
          }
        }
      );
    });
  },
  changePassword(oldPassword: string, newPassword: string) {
    return new Promise((resolve, reject) => {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return reject("Not signed in");

      currentUser.getSession(
        (err: null | Error, session: null | CognitoUserSession) => {
          if (err) return reject(err.message);
          if (session) {
            currentUser.changePassword(
              oldPassword,
              newPassword,
              (err, result) => {
                if (err) return reject(err.message);
                if (result) return resolve(result);
              }
            );
          }
        }
      );
    });
  },
  forgotPassword(username: string) {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser()) return reject("Already signed in");
      const isValidEmail = String(username)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
      if (!isValidEmail) {
        return reject('Email is invalid')
      }
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool(),
      });

      cognitoUser.forgotPassword({
        onFailure: (err) => reject(err.message),
        onSuccess: (result) => resolve(result),
      });
    });
  },
  confirmPassword(code: string, email: string, password: string) {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser()) return reject("Already signed in");

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: this.userPool(),
      });

      cognitoUser.confirmPassword(code, password, {
        onFailure: (err) => reject(err.message),
        onSuccess: (result) => resolve(result),
      });
    });
  },
  resendConfirmationCode(username: string) {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser()) return reject("Already signed in");

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool(),
      });

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) return reject(err.message);
        if (result) return resolve(result);
      });
    });
  },
  confirmRegistration(code: string, username: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser()) return reject("Already signed in");

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: this.userPool(),
      });

      cognitoUser.confirmRegistration(code, true, (err: Error, result) => {
        if (err) return reject(err.message);
        if (result) return resolve(result);
      });
    });
  },
  getSession(): Promise<CognitoUserSession | null> {
    return new Promise((resolve, reject) => {
      const currentUser = this.getCurrentUser();
      if (!currentUser) return resolve(null);

      currentUser.getSession(
        (err: null | Error, session: null | CognitoUserSession) => {
          if (err) return reject(err.message);
          if (!session) return resolve(null);
          if (!session.isValid()) return resolve(null);

          AWS.config.region = AWS_REGION;

          AWS.config.credentials = new AWS.CognitoIdentityCredentials({
            IdentityPoolId: AWS_IDENTITY_POOL_ID,
            Logins: {
              [`cognito-idp.${AWS_REGION}.amazonaws.com/${AWS_USER_POOL_ID}`]:
                session.getIdToken().getJwtToken(),
            },
          });

          (
            AWS.config.credentials as AWS.CognitoIdentityCredentials
          ).clearCachedId();

          (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(
            (err) => {
              if (err) reject(err.message);
            }
          );

          resolve(session);
        }
      );
    });
  },
  isAdmin(session: CognitoUserSession): boolean {
    return (
      session
        .getIdToken()
        .decodePayload()
        ?.["cognito:groups"]?.includes("ADMIN") || false
    );
  },
  async refreshToken(): Promise<boolean> {
    const cognitoUser = this.getCurrentUser();
    if (!cognitoUser) return false;

    const refreshToken = (await this.getSession())?.getRefreshToken();
    if (!refreshToken) return false;

    return new Promise((resolve, reject) => {
      if (
        !(
          AWS.config.credentials as AWS.CognitoIdentityCredentials
        )?.needsRefresh()
      )
        return resolve(false);

      cognitoUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          this.signOut();
          return reject(err.message);
        }

        (
          (AWS.config.credentials as AWS.CognitoIdentityCredentials)
            .params as CognitoIdentity.Types.GetIdInput
        ).Logins![
          `cognito-idp.${AWS_REGION}.amazonaws.com/${AWS_USER_POOL_ID}`
        ] = session.getIdToken().getJwtToken();

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(
          (err) => {
            if (err) return reject(err.message);
            else return resolve(true);
          }
        );
      });
    });
  },
};
